import { z } from 'zod';

/**
 * postMessage protocol between the portal and the SDK / preview boot page.
 * Every message: { ns: "rv", v: 1, type, id?, payload }.
 * Receivers MUST validate exact origin, event.source and this schema.
 */
export const RV_NS = 'rv';
export const RV_PROTOCOL_VERSION = 1;

export const anchorSchema = z.object({
  anchorType: z.enum(['element', 'limited', 'page']),
  reviewElementId: z.string().max(200).nullable().optional(),
  selectors: z.array(z.string().max(2000)).max(6),
  fingerprint: z
    .object({
      tag: z.string().max(40),
      textHash: z.string().max(128).optional(),
      textPrefix: z.string().max(64).optional(),
      attrs: z.record(z.string(), z.string().max(300)).optional(),
      classes: z.array(z.string().max(100)).max(12).optional(),
      siblingIndex: z.number().int().min(0).max(100000).optional(),
      depth: z.number().int().min(0).max(500).optional(),
    })
    .nullable(),
  offset: z.object({ xPct: z.number().min(0).max(1), yPct: z.number().min(0).max(1) }),
  rect: z.object({ x: z.number(), y: z.number(), w: z.number().min(0), h: z.number().min(0) }),
  viewport: z.object({ w: z.number().int().min(0), h: z.number().int().min(0), dpr: z.number().min(0).max(10).optional() }),
  scroll: z.object({ x: z.number(), y: z.number() }),
  docSize: z.object({ w: z.number().min(0), h: z.number().min(0) }),
  sectionKey: z.string().max(100).nullable().optional(),
  limitedReason: z.string().max(200).nullable().optional(),
  // 2: a pin made by the current toolbar (re-found inside its landmarks; older pins have none).
  v: z.literal(2).optional(),
  landmarks: z.array(z.string().max(500)).max(3).optional(),
  // A box marked inside the element (fractions 0..1), e.g. on an embedded Calendly frame.
  region: z.object({ x: z.number().min(0).max(1), y: z.number().min(0).max(1), w: z.number().min(0).max(1), h: z.number().min(0).max(1) }).optional(),
});
export type AnchorInput = z.infer<typeof anchorSchema>;

const envelope = <T extends string, P extends z.ZodTypeAny>(type: T, payload: P) =>
  z.object({ ns: z.literal(RV_NS), v: z.literal(RV_PROTOCOL_VERSION), type: z.literal(type), id: z.string().max(64).optional(), payload });

export const pinSchema = z.object({
  issueId: z.string(),
  number: z.number().int(),
  status: z.string(),
  category: z.string(),
  anchor: anchorSchema,
  selected: z.boolean().optional(),
  // Hover card text (plain text; the SDK renders it with textContent).
  preview: z
    .object({ author: z.string().max(120), when: z.string().max(60), body: z.string().max(600), replies: z.number().int().min(0), canReply: z.boolean(), replyHint: z.string().max(120).optional() })
    .optional(),
});
export type PinInput = z.infer<typeof pinSchema>;

const cursorPoint = z.object({ sel: z.string().max(300).optional(), ex: z.number().min(0).max(1), ey: z.number().min(0).max(1), dx: z.number().min(0).max(1), dy: z.number().min(0).max(1e7), dh: z.number().min(0).max(1e7) });

/** Follow: something the followed person did on the page (a click, typing in a field …). */
const coAct = z.object({ a: z.enum(['click', 'input', 'check', 'select', 'key', 'focus', 'submit']), sel: z.string().max(300).optional(), value: z.string().max(500).optional(), checked: z.boolean().optional(), key: z.literal('Escape').optional(), safe: z.boolean().optional(), label: z.string().max(80).optional() });

/* Frame → portal */
export const frameToPortal = z.discriminatedUnion('type', [
  envelope('boot-ready', z.object({ releaseKey: z.string().optional() })),
  envelope('preview-auth', z.object({ ok: z.boolean(), reason: z.string().max(200).optional() })),
  envelope('embed-auth-unavailable', z.object({ reason: z.string().max(200) })),
  envelope('agent:ready', z.object({ url: z.string().max(4000), title: z.string().max(500), sdkVersion: z.string().max(40), loadedLate: z.boolean().optional() })),
  envelope('agent:location', z.object({ url: z.string().max(4000), title: z.string().max(500) })),
  envelope('agent:target-selected', z.object({ anchor: anchorSchema, url: z.string().max(4000) })),
  envelope('agent:pin-clicked', z.object({ issueId: z.string() })),
  envelope('agent:pin-status', z.object({ statuses: z.array(z.object({ issueId: z.string(), resolution: z.enum(['ok', 'changed', 'missing', 'ambiguous']) })).max(2000) })),
  envelope('agent:capture-result', z.object({ ok: z.boolean(), reason: z.string().max(300).optional(), dataUrl: z.string().max(20_000_000).optional(), width: z.number().optional(), height: z.number().optional() })),
  envelope('agent:cancel', z.object({})),
  // Compare view: the frame reports its scroll position once sync is enabled.
  envelope('agent:scroll', z.object({ x: z.number(), y: z.number() })),
  // Live cursors (after the portal turns them on): where this person's pointer is, or that it left.
  envelope('agent:pointer', z.object({ p: cursorPoint })),
  envelope('agent:pointer-away', z.object({})),
  // Follow: where this person is looking (only while someone follows them), and that they took over
  // (scrolled, clicked or typed) while following someone.
  envelope('agent:view', z.object({ p: cursorPoint })),
  envelope('agent:own-input', z.object({})),
  envelope('agent:act', coAct),
  // Quick reply typed in a pin's hover card; the portal posts it with its own session.
]);
export type FrameToPortal = z.infer<typeof frameToPortal>;

/* Portal → frame */
export const portalToFrame = z.discriminatedUnion('type', [
  envelope('preview-launch', z.object({ code: z.string().min(16).max(200), path: z.string().max(2000) })),
  envelope('hello', z.object({})),
  envelope('agent:init', z.object({ mode: z.enum(['browse', 'comment']), pins: z.array(pinSchema).max(2000), maskSelectors: z.array(z.string()).max(50).optional() })),
  envelope('agent:set-mode', z.object({ mode: z.enum(['browse', 'comment']) })),
  envelope('agent:set-pins', z.object({ pins: z.array(pinSchema).max(2000) })),
  envelope('agent:focus-pin', z.object({ issueId: z.string() })),
  envelope('agent:clear-selection', z.object({})),
  // crop: screenshot only the selected element (Pick mode).
  envelope('agent:capture', z.object({ anchor: anchorSchema.nullable(), crop: z.boolean().optional() })),
  envelope('agent:sync-scroll', z.object({ enabled: z.boolean() })),
  envelope('agent:scroll-to', z.object({ x: z.number(), y: z.number() })),
  // Live cursors: start / stop sending this person's pointer; the others' cursors to draw.
  envelope('agent:cursors', z.object({ enabled: z.boolean(), share: z.boolean() })),
  envelope('agent:cursor', z.object({ id: z.string().max(40), name: z.string().max(120), color: z.string().max(20), s: z.number().int(), p: cursorPoint })),
  envelope('agent:cursor-away', z.object({ id: z.string().max(40) })),
  envelope('agent:cursor-gone', z.object({ id: z.string().max(40) })),
  envelope('agent:cursors-reset', z.object({})),
  // Follow: open the followed person's page (same website only), scroll to where they look, send my
  // view (someone follows me), and tell me when the person here takes over.
  envelope('agent:navigate', z.object({ url: z.string().max(4000) })),
  envelope('agent:cursor-draft', z.object({ id: z.string().max(40), name: z.string().max(120), color: z.string().max(20), text: z.string().max(1000).nullable(), p: cursorPoint.nullable() })),
  envelope('agent:scroll-to-point', z.object({ p: cursorPoint })),
  envelope('agent:lead-on', z.object({ on: z.boolean() })),
  envelope('agent:follow-guard', z.object({ on: z.boolean() })),
  envelope('agent:play-act', coAct.extend({ name: z.string().max(120), color: z.string().max(20) })),
]);
export type PortalToFrame = z.infer<typeof portalToFrame>;

export function makeMessage<T extends string>(type: T, payload: unknown, id?: string) {
  return { ns: RV_NS, v: RV_PROTOCOL_VERSION, type, id, payload };
}
